import React, { useCallback, useEffect, useRef, useState } from "react";
import { searchPlayers } from "../data/playerLoader";

/**
 * PlayerSearch — type-to-search fallback for player nomination.
 *
 * Props:
 *   onSelect(player)  — called when a player is chosen from the dropdown.
 *   disabled           — disables the input when true.
 */
export default function PlayerSearch({ onSelect, disabled = false, micListening = false, onMicToggle, micDisabled = false }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Run fuzzy search whenever the query changes.
  useEffect(() => {
    if (query.trim().length === 0) {
      setResults([]);
      setOpen(false);
      setActiveIndex(-1);
      return;
    }

    const matches = searchPlayers(query, 5);
    setResults(matches);
    setOpen(matches.length > 0);
    setActiveIndex(matches.length > 0 ? 0 : -1);
  }, [query]);

  const selectPlayer = useCallback(
    player => {
      setQuery("");
      setResults([]);
      setOpen(false);
      setActiveIndex(-1);
      onSelect(player);
    },
    [onSelect]
  );

  const handleKeyDown = useCallback(
    event => {
      if (!open || results.length === 0) {
        return;
      }

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          setActiveIndex(prev =>
            prev < results.length - 1 ? prev + 1 : 0
          );
          break;

        case "ArrowUp":
          event.preventDefault();
          setActiveIndex(prev =>
            prev > 0 ? prev - 1 : results.length - 1
          );
          break;

        case "Enter":
          event.preventDefault();
          if (activeIndex >= 0 && activeIndex < results.length) {
            selectPlayer(results[activeIndex].item);
          }
          break;

        case "Escape":
          event.preventDefault();
          setOpen(false);
          setActiveIndex(-1);
          break;

        default:
          break;
      }
    },
    [activeIndex, open, results, selectPlayer]
  );

  // Scroll the active option into view when navigating with the keyboard.
  useEffect(() => {
    if (activeIndex < 0 || !listRef.current) {
      return;
    }

    const activeElement = listRef.current.children[activeIndex];
    if (activeElement) {
      activeElement.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex]);

  return (
    <div className="player-search">
      <label className="player-search__label" htmlFor="player-search-input">
        Search by Name
      </label>

      <div className="player-search__field-wrapper">
        <input
          ref={inputRef}
          id="player-search-input"
          className="player-search__input"
          type="text"
          placeholder="Type a player name…"
          autoComplete="off"
          value={query}
          disabled={disabled}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (results.length > 0) {
              setOpen(true);
            }
          }}
          onBlur={() => {
            // Delay closing so a click on a dropdown option registers first.
            setTimeout(() => setOpen(false), 180);
          }}
          role="combobox"
          aria-expanded={open}
          aria-controls="player-search-listbox"
          aria-activedescendant={
            activeIndex >= 0 ? `player-search-option-${activeIndex}` : undefined
          }
        />
        {onMicToggle && (
          <button
            type="button"
            className={`nomination-mic-button nomination-mic-button--inline${micListening ? " nomination-mic-button--active" : ""}`}
            onClick={onMicToggle}
            disabled={micDisabled}
            aria-label={micListening ? "Stop microphone" : "Start microphone"}
          >
            <svg className="nomination-mic-button__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="9" y="1" width="6" height="12" rx="3" />
              <path d="M19 10v1a7 7 0 0 1-14 0v-1" />
              <line x1="12" y1="19" x2="12" y2="23" />
              <line x1="8" y1="23" x2="16" y2="23" />
            </svg>
          </button>
        )}
      </div>

      {open && results.length > 0 && (
        <ul
          ref={listRef}
          id="player-search-listbox"
          className="player-search__dropdown"
          role="listbox"
        >
          {results.map((match, index) => (
            <li
              key={match.item.id}
              id={`player-search-option-${index}`}
              className={`player-search__option${
                index === activeIndex ? " player-search__option--active" : ""
              }`}
              role="option"
              aria-selected={index === activeIndex}
              onMouseDown={e => {
                // Prevent the input blur from firing before we process the click.
                e.preventDefault();
              }}
              onClick={() => selectPlayer(match.item)}
              onMouseEnter={() => setActiveIndex(index)}
            >
              <span className="player-search__option-name">
                {match.item.name}
              </span>
              <span className="player-search__option-meta">
                {match.item.position} &middot; {match.item.team}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
